import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../api";
import ImageUploader from "./ImageUploader";

const AMENITIES = ["Parking", "Furnished", "Lift", "Gym", "Swimming Pool", "Clubhouse", "Power Backup", "Garden", "Kids Play Area", "Yoga Deck", "Party Lawn", "24x7 Security"];
const TYPES = ["Flat", "House", "Commercial"];
const FURNISHING = ["Unfurnished", "Semi-Furnished", "Furnished"];
const FACING = ["East", "West", "North", "South", "North-East", "North-West", "South-East", "South-West"];
const STATUS = ["Ready to Move", "Under Construction", "New Launch"];

const INPUT = "w-full bg-neutral-50 border border-neutral-200 rounded-xl px-4 py-3 text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-brand-500";
const LABEL = "block text-sm text-neutral-600 mb-1.5";
const SECTION = "text-lg font-bold text-navy pt-2";

// Module-scope field components so their identity is stable across renders
function TextField({ label, value, onChange, type = "text", ph = "", testid, required }) {
  return (
    <div>
      <label className={LABEL}>{label}</label>
      <input type={type} required={required} placeholder={ph} className={INPUT} value={value} onChange={onChange} data-testid={testid} />
    </div>
  );
}

function SelectField({ label, value, onChange, options, placeholder, testid }) {
  return (
    <div>
      <label className={LABEL}>{label}</label>
      <select className={INPUT} value={value} onChange={onChange} data-testid={testid}>
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

const empty = {
  title: "", property_type: "Flat", listing_type: "Rent", city: "Pune", area: "",
  address: "", price: "", size_sqft: "", carpet_area: "", price_per_sqft: "",
  bedrooms: "", bathrooms: "", balconies: "", bhk_config: "", furnishing: "", facing: "",
  floor_number: "", total_floors: "", total_towers: "", total_units: "",
  construction_status: "Ready to Move", possession_date: "", age_of_property: "", available_from: "",
  builder_name: "", rera_id: "", maintenance_charge: "",
  amenities: [], description: "", image: "", images: [],
  contact_name: "", contact_phone: "", contact_email: "",
};

const num = (v) => (v === "" || v == null ? null : parseFloat(v));
const int = (v) => (v === "" || v == null ? null : parseInt(v));

export default function PropertyForm({ initial, onSaved, onCancel }) {
  const [form, setForm] = useState(empty);
  const [cities, setCities] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/meta/cities").then((r) => setCities(r.data.cities)).catch(() => {});
    if (initial) {
      const filled = { ...empty };
      Object.keys(empty).forEach((k) => {
        if (initial[k] !== undefined && initial[k] !== null) filled[k] = initial[k];
      });
      filled.images = initial.images || (initial.image ? [initial.image] : []);
      filled.amenities = initial.amenities || [];
      setForm(filled);
    }
  }, [initial]);

  const set = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };
  const toggleAmenity = (a) =>
    setForm((f) => ({
      ...f,
      amenities: f.amenities.includes(a) ? f.amenities.filter((x) => x !== a) : [...f.amenities, a],
    }));

  const areas = cities.find((c) => c.name === form.city)?.areas || [];

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const gallery = form.images;
    const payload = {
      title: form.title, property_type: form.property_type, listing_type: form.listing_type,
      city: form.city, area: form.area, address: form.address,
      price: num(form.price) || 0,
      size_sqft: num(form.size_sqft), carpet_area: num(form.carpet_area), price_per_sqft: num(form.price_per_sqft),
      bedrooms: int(form.bedrooms), bathrooms: int(form.bathrooms), balconies: int(form.balconies),
      bhk_config: form.bhk_config, furnishing: form.furnishing, facing: form.facing,
      floor_number: int(form.floor_number), total_floors: int(form.total_floors),
      total_towers: int(form.total_towers), total_units: int(form.total_units),
      construction_status: form.construction_status, possession_date: form.possession_date,
      age_of_property: form.age_of_property, available_from: form.available_from,
      builder_name: form.builder_name, rera_id: form.rera_id, maintenance_charge: num(form.maintenance_charge),
      amenities: form.amenities, description: form.description,
      image: form.image || gallery[0] || "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=900&q=80",
      images: gallery,
      contact_name: form.contact_name, contact_phone: form.contact_phone, contact_email: form.contact_email,
    };
    try {
      if (initial?.id) await api.put(`/properties/${initial.id}`, payload);
      else await api.post("/properties", payload);
      setLoading(false);
      onSaved && onSaved();
    } catch (e2) {
      setLoading(false);
      setErr(formatApiError(e2.response?.data?.detail));
    }
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="property-form">
      <h3 className={SECTION}>Basic details</h3>
      <TextField label="Title *" required value={form.title} onChange={set("title")} ph="e.g. Esbee Azura by Esbee Realty" testid="pf-title" />
      <div className="grid sm:grid-cols-4 gap-4">
        <SelectField label="Property Type *" value={form.property_type} onChange={set("property_type")} options={TYPES} testid="pf-type" />
        <SelectField label="Listing *" value={form.listing_type} onChange={set("listing_type")} options={["Rent", "Buy"]} testid="pf-listing" />
        <TextField label="Price (₹) *" required type="number" value={form.price} onChange={set("price")} ph="e.g. 7388000" testid="pf-price" />
        <SelectField label="Status" value={form.construction_status} onChange={set("construction_status")} options={STATUS} testid="pf-status" />
      </div>

      <h3 className={SECTION}>Location</h3>
      <div className="grid sm:grid-cols-3 gap-4">
        <SelectField label="City *" value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value, area: "" }))} options={cities.length ? cities.map((c) => c.name) : ["Pune"]} testid="pf-city" />
        <div>
          <label className={LABEL}>Area *</label>
          <input required list="pf-areas" placeholder="e.g. Keshavnagar" className={INPUT} value={form.area} onChange={set("area")} data-testid="pf-area" />
          <datalist id="pf-areas">{areas.map((a) => <option key={a} value={a} />)}</datalist>
        </div>
        <SelectField label="Facing" value={form.facing} onChange={set("facing")} options={FACING} placeholder="Select" testid="pf-facing" />
      </div>
      <TextField label="Address" value={form.address} onChange={set("address")} ph="Full address" testid="pf-address" />

      <h3 className={SECTION}>Area & configuration</h3>
      <div className="grid sm:grid-cols-3 gap-4">
        <TextField label="Carpet Area (sq.ft)" type="number" value={form.carpet_area} onChange={set("carpet_area")} ph="e.g. 711" testid="pf-carpet" />
        <TextField label="Built-up Area (sq.ft)" type="number" value={form.size_sqft} onChange={set("size_sqft")} ph="e.g. 735" testid="pf-size" />
        <TextField label="Price / sq.ft (₹)" type="number" value={form.price_per_sqft} onChange={set("price_per_sqft")} ph="e.g. 10052" testid="pf-ppsf" />
        <TextField label="Bedrooms (BHK)" type="number" value={form.bedrooms} onChange={set("bedrooms")} ph="e.g. 2" testid="pf-bedrooms" />
        <TextField label="Bathrooms" type="number" value={form.bathrooms} onChange={set("bathrooms")} ph="e.g. 2" testid="pf-bathrooms" />
        <TextField label="Balconies" type="number" value={form.balconies} onChange={set("balconies")} ph="e.g. 2" testid="pf-balconies" />
        <TextField label="BHK Configuration" value={form.bhk_config} onChange={set("bhk_config")} ph="e.g. 2, 3 BHK" testid="pf-bhk" />
        <SelectField label="Furnishing" value={form.furnishing} onChange={set("furnishing")} options={FURNISHING} placeholder="Select" testid="pf-furnishing" />
        <TextField label="Maintenance / month (₹)" type="number" value={form.maintenance_charge} onChange={set("maintenance_charge")} ph="e.g. 3700" testid="pf-maintenance" />
      </div>

      <h3 className={SECTION}>Building & project</h3>
      <div className="grid sm:grid-cols-4 gap-4">
        <TextField label="Floor Number" type="number" value={form.floor_number} onChange={set("floor_number")} ph="e.g. 6" testid="pf-floor" />
        <TextField label="Total Floors" type="number" value={form.total_floors} onChange={set("total_floors")} ph="e.g. 12" testid="pf-total-floors" />
        <TextField label="Total Towers" type="number" value={form.total_towers} onChange={set("total_towers")} ph="e.g. 2" testid="pf-towers" />
        <TextField label="Total Units" type="number" value={form.total_units} onChange={set("total_units")} ph="e.g. 180" testid="pf-units" />
        <TextField label="Builder / Developer" value={form.builder_name} onChange={set("builder_name")} ph="e.g. Esbee Realty" testid="pf-builder" />
        <TextField label="RERA ID" value={form.rera_id} onChange={set("rera_id")} ph="e.g. PR12600..." testid="pf-rera" />
        <TextField label="Possession" value={form.possession_date} onChange={set("possession_date")} ph="e.g. Aug 2028" testid="pf-possession" />
        <TextField label="Age of Property" value={form.age_of_property} onChange={set("age_of_property")} ph="e.g. New Launch" testid="pf-age" />
      </div>
      {form.listing_type === "Rent" && (
        <TextField label="Available From" value={form.available_from} onChange={set("available_from")} ph="e.g. Immediate / Jan 2027" testid="pf-available" />
      )}

      <h3 className={SECTION}>Photos</h3>
      <ImageUploader value={form.images} onChange={(imgs) => setForm((f) => ({ ...f, images: imgs }))} />

      <h3 className={SECTION}>Amenities</h3>
      <div className="flex flex-wrap gap-2">
        {AMENITIES.map((a) => (
          <button type="button" key={a} onClick={() => toggleAmenity(a)} data-testid={`pf-amenity-${a}`}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
              form.amenities.includes(a) ? "bg-brand-500 text-white border-brand-500" : "border-neutral-300 text-neutral-600 hover:border-neutral-500"
            }`}>
            {a}
          </button>
        ))}
      </div>

      <div>
        <label className={LABEL}>Description</label>
        <textarea rows={3} placeholder="Describe the property…" className={INPUT} value={form.description} onChange={set("description")} data-testid="pf-description" />
      </div>

      <h3 className={SECTION}>Contact</h3>
      <div className="grid sm:grid-cols-3 gap-4">
        <TextField label="Contact Name" value={form.contact_name} onChange={set("contact_name")} ph="Name" testid="pf-contact-name" />
        <TextField label="Contact Phone" value={form.contact_phone} onChange={set("contact_phone")} ph="Phone" testid="pf-contact-phone" />
        <TextField label="Contact Email" value={form.contact_email} onChange={set("contact_email")} ph="Email" testid="pf-contact-email" />
      </div>

      {err && <p className="text-red-500 text-sm" data-testid="pf-error">{err}</p>}

      <div className="flex gap-3 pt-2">
        <button type="submit" disabled={loading} data-testid="pf-submit-btn"
          className="px-6 py-3 font-semibold text-white bg-gradient-to-r from-brand-500 to-brand-600 rounded-xl hover:shadow-soft transition-all active:scale-95 disabled:opacity-60">
          {loading ? "Saving…" : initial?.id ? "Update Property" : "List Property"}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="px-6 py-3 text-neutral-600 border border-neutral-300 rounded-xl hover:text-neutral-900">
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
